import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ApiError } from '@/api/errors'
import type {
  CalendarEntryKind,
  CalendarEntryResponse,
  Paginated,
} from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { Page, PageHeader } from '@/components/Page'
import { Icon } from '@/components/Icon'
import { SkeletonList } from '@/components/Skeleton'
import { formatVisitDate, parseCalendarDate, todayIso } from '@/lib/dates'
import { useModals } from '@/app/modals/ModalContext'
import { calendar as api } from '@/features/callers/api'
import { cx } from '@/lib/cx'
import { useMinWidth } from '@/lib/useMediaQuery'
import { errorMessage } from '@/api/messages'

/** Each kind says what it is in words. Colour is the second signal, never the
 *  only one — four dots in four colours is a puzzle. */
/* Four distinct hues, each also carrying a word in the legend and a title on
   every entry — colour is never the only signal. */
const KIND_META: Record<CalendarEntryKind, { label: string; dot: string }> = {
  follow_up: { label: 'Patient due', dot: 'bg-chart-line' },
  appointment: { label: 'Booked', dot: 'bg-success' },
  // What a caller asked for and nobody has committed to. It used to share the
  // 'Appointment' dot, which made a wish look like a promise.
  enquiry: { label: 'Requested', dot: 'bg-accent' },
  caller_follow_up: { label: 'Call back', dot: 'bg-warning' },
  travel: { label: 'Travel', dot: 'bg-fg-subtle' },
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** The whole entry, for the tooltip and the accessible name. */
function describe(label: string, entry: CalendarEntryResponse): string {
  return [`${label}: ${entry.title}`, entry.subtitle]
    .filter(Boolean)
    .join(' — ')
}

function iso(year: number, month: number, day: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${year}-${pad(month + 1)}-${pad(day)}`
}

/** Monday-first offset: getDay() is Sunday-first, and a clinic week is not. */
function leadingBlanks(year: number, month: number): number {
  return (new Date(year, month, 1).getDay() + 6) % 7
}

/** One month of everything the clinic has committed to. Booked appointments are
 *  real records; the rest is derived from what the clinic already keeps. */
export function CalendarScreen() {
  const today = todayIso()
  const modals = useModals()
  const [cursor, setCursor] = useState(() => {
    const now = parseCalendarDate(today)
    return { year: now.getFullYear(), month: now.getMonth() }
  })

  // A month grid needs seven readable columns. 360px does not have them, so
  // below `md` the same month is an agenda: the days that have something on
  // them, in order, at full width.
  const wide = useMinWidth('md')
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate()
  const from = iso(cursor.year, cursor.month, 1)
  const to = iso(cursor.year, cursor.month, daysInMonth)

  const month = useQuery<Paginated<CalendarEntryResponse>, ApiError>({
    queryKey: queryKeys.calendar(from, to),
    queryFn: ({ signal }) => api.month(from, to, signal),
  })

  // One pass into day buckets; the grid then reads straight out of it.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEntryResponse[]>()
    for (const entry of month.data?.data ?? []) {
      const bucket = map.get(entry.on)
      if (bucket) bucket.push(entry)
      else map.set(entry.on, [entry])
    }
    return map
  }, [month.data])

  // Chunked into weeks so the grid can carry real rows. `null` is a leading or
  // trailing blank; padding the last week keeps every row seven cells wide.
  const weeks = useMemo(() => {
    const cells: (string | null)[] = [
      ...Array.from(
        { length: leadingBlanks(cursor.year, cursor.month) },
        () => null,
      ),
      ...Array.from({ length: daysInMonth }, (_, i) =>
        iso(cursor.year, cursor.month, i + 1),
      ),
    ]
    while (cells.length % 7 !== 0) cells.push(null)
    const rows: (string | null)[][] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [cursor, daysInMonth])

  const label = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(cursor.year, cursor.month, 1))

  function shift(months: number) {
    setCursor((c) => {
      const next = new Date(c.year, c.month + months, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Booking"
        title="Calendar"
        description="Booked appointments, patients due back, enquiries to ring, and where the doctor is. Click a day to book."
        actions={
          <div className="flex items-center gap-3">
            <Button variant="secondary" aria-label="Previous month" onClick={() => shift(-1)}>
              <Icon name="chevronLeft" size="1rem" />
            </Button>
            <span className="min-w-40 text-center text-lg font-semibold" aria-live="polite">
              {label}
            </span>
            <Button variant="secondary" aria-label="Next month" onClick={() => shift(1)}>
              <Icon name="chevronRight" size="1rem" />
            </Button>
          </div>
        }
      />

      {month.error && (
        <Banner tone={month.error.isNetwork ? 'warning' : 'danger'}>
          {errorMessage(month.error)}
        </Banner>
      )}

      <ul className="m-0 mb-4 flex list-none flex-wrap gap-x-5 gap-y-2 p-0 text-sm text-fg-muted [&>li]:flex [&>li]:items-center [&>li]:gap-2">
        {(Object.keys(KIND_META) as CalendarEntryKind[]).map((kind) => (
          <li key={kind}>
            <span
              className={cx('size-2.5 flex-none rounded-full', KIND_META[kind].dot)}
            />
            {KIND_META[kind].label}
          </li>
        ))}
      </ul>

      <Card title={label} flush>
        {month.isLoading ? (
          <div className="p-5">
            <SkeletonList rows={6} label='Loading calendar' />
          </div>
        ) : !wide ? (
          <Agenda
            label={label}
            days={Array.from({ length: daysInMonth }, (_, i) => iso(cursor.year, cursor.month, i + 1))}
            byDay={byDay}
            today={today}
            onBook={(day) => modals.open({ kind: 'appointment', date: day })}
          />
        ) : (
          <div
            className="grid grid-cols-7 border-t border-line-soft"
            role="grid"
            aria-label={`Calendar for ${label}`}
          >
            {/* display: contents on .week — the rows are real to assistive
                technology and invisible to the CSS grid. */}
            <div className="contents" role="row">
              {WEEKDAYS.map((day) => (
                <div key={day} className="bg-subtle p-2 text-center text-xs font-medium text-fg-muted" role="columnheader">
                  {day}
                </div>
              ))}
            </div>

            {weeks.map((week, weekIndex) => (
              <div
                key={week.find(Boolean) ?? `week-${weekIndex}`}
                className="contents"
                role="row"
              >
                {week.map((day, dayIndex) => {
                  if (day === null) {
                    return (
                      <div
                        key={`blank-${weekIndex}-${dayIndex}`}
                        className="flex min-h-26 min-w-0 flex-col gap-1 border-r border-b border-line-soft bg-subtle p-2 nth-[7n]:border-r-0"
                        role="gridcell"
                      />
                    )
                  }
                  const entries = byDay.get(day) ?? []
                  return (
                    <div
                      key={day}
                      role="gridcell"
                      className={cx(
                        'group/day flex min-h-26 min-w-0 flex-col gap-1 border-r border-b border-line-soft p-2 nth-[7n]:border-r-0',
                        day === today && 'bg-accent-soft [&_[data-day]]:text-accent',
                      )}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span data-day className="text-xs font-semibold text-fg-muted tabular-nums">
                          {Number(day.slice(8))}
                        </span>
                        {/* Click the day to book on it, the way a PMS works.
                            Hidden on days already gone: the server refuses a
                            past date, and offering it only to refuse is worse
                            than not offering it. Always rendered on touch,
                            where there is no hover to reveal it. */}
                        {day >= today && (
                          <button
                            type="button"
                            aria-label={`Book an appointment on ${formatVisitDate(day)}`}
                            title={`Book on ${formatVisitDate(day)}`}
                            onClick={() => modals.open({ kind: 'appointment', date: day })}
                            className="grid size-6 flex-none place-items-center rounded-md text-fg-subtle opacity-0 transition-opacity duration-150 ease-standard hover:bg-hover hover:text-accent focus-visible:opacity-100 group-hover/day:opacity-100 max-md:opacity-100"
                          >
                            <Icon name="plus" size="0.875rem" />
                          </button>
                        )}
                      </div>
                      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 min-w-0">
                        {entries.map((entry, index) => {
                          const meta = KIND_META[entry.kind]
                          // Title only inside the cell. A name and a subtitle
                          // sharing one 90px line truncated BOTH to nothing; the
                          // full text lives in the tooltip and on the linked page.
                          const body = (
                            <>
                              <span
                                className={cx('size-2.5 flex-none rounded-full', meta.dot)}
                              />
                              <span className="truncate">
                                {entry.title}
                              </span>
                            </>
                          )
                          // 24px, not the app's 44px floor: a month of seven
                          // columns cannot give three entries in a cell 44px
                          // each without becoming a month of four days. This
                          // clears the 2.5.8 minimum, and the posture where a
                          // finger is doing the pointing — the phone — gets
                          // the agenda, where every entry is a full 44px row.
                          //
                          // An appointment goes to the day it was booked on,
                          // not to the patient's record: someone clicking a
                          // booking wants to see the booking, and who else is
                          // in that day.
                          const to = entry.kind === 'appointment'
                            ? `/appointments?on=${entry.on}`
                            : entry.patient_id
                            ? `/patients/${entry.patient_id}`
                            : entry.caller_id
                              ? `/callers/${entry.caller_id}`
                              : null
                          return (
                            <li key={`${entry.kind}-${entry.title}-${index}`}>
                              {to ? (
                                <Link
                                  className="flex min-h-6 min-w-0 items-center gap-1 overflow-hidden rounded-sm px-1 py-1 text-xs text-fg no-underline hover:bg-hover hover:underline"
                                  to={to}
                                  title={describe(meta.label, entry)}
                                >
                                  {body}
                                </Link>
                              ) : (
                                <span
                                  className="flex min-h-6 min-w-0 items-center gap-1 overflow-hidden rounded-sm px-1 py-1 text-xs text-fg no-underline hover:bg-hover hover:underline"
                                  title={describe(meta.label, entry)}
                                >
                                  {body}
                                </span>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </Card>
    </Page>
  )
}

/** Where an entry goes when it is clicked. An appointment goes to the day it
 *  was booked on, not to the patient's record: someone clicking a booking wants
 *  to see the booking, and who else is in that day. */
function entryHref(entry: CalendarEntryResponse): string | null {
  if (entry.kind === 'appointment') return `/appointments?on=${entry.on}`
  if (entry.patient_id) return `/patients/${entry.patient_id}`
  if (entry.caller_id) return `/callers/${entry.caller_id}`
  return null
}

/**
 * The month as a list, for a phone.
 *
 * Only the days that have something on them: thirty empty cells is not
 * information, and scrolling past them to find the two days that matter is the
 * opposite of what this screen is for. Each entry is a full-width row at the
 * 44px floor, with the subtitle the grid cell had no room to show.
 */
function Agenda({
  label,
  days,
  byDay,
  today,
  onBook,
}: {
  label: string
  days: string[]
  byDay: Map<string, CalendarEntryResponse[]>
  today: string
  onBook: (day: string) => void
}) {
  // The grid shows a whole month at once; a list cannot, so on the month that
  // contains today it opens at today. Arriving on the 29th to twenty-eight days
  // of finished appointments means scrolling past the month to reach the point
  // of the screen. The earlier days are still here, one tap away.
  const [showPast, setShowPast] = useState(false)
  const all = days.filter((day) => (byDay.get(day)?.length ?? 0) > 0)
  const past = all.filter((day) => day < today)
  const opensAtToday = !showPast && past.length > 0 && all.some((day) => day >= today)
  const busy = opensAtToday ? all.filter((day) => day >= today) : all

  if (all.length === 0) {
    return (
      <p className="border-t border-line-soft px-4 py-8 text-center text-sm text-fg-muted">
        Nothing booked or due in {label}.
      </p>
    )
  }

  const heading = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

  return (
    <div className="border-t border-line-soft" aria-label={`Agenda for ${label}`}>
      {opensAtToday && (
        <button
          type="button"
          onClick={() => setShowPast(true)}
          className="flex min-h-touch w-full items-center justify-center gap-2 border-b border-line-soft px-4 text-sm font-medium text-accent hover:bg-hover"
        >
          <Icon name="chevronUp" size="1rem" />
          {past.length === 1
            ? 'Show 1 earlier day this month'
            : `Show ${past.length} earlier days this month`}
        </button>
      )}
      {busy.map((day) => {
        const entries = byDay.get(day) ?? []
        return (
          <section key={day}>
            <div
              className={cx(
                'flex min-h-touch items-center justify-between gap-3 border-b border-line-soft px-4 py-2',
                day === today ? 'bg-accent-soft' : 'bg-subtle',
              )}
            >
              <h3
                className={cx(
                  'text-sm font-semibold tabular-nums',
                  day === today ? 'text-accent' : 'text-fg',
                )}
              >
                {heading.format(parseCalendarDate(day))}
                {day === today && <span className="ml-2 font-normal">Today</span>}
              </h3>
              {/* The server refuses a past date, so a day already gone offers
                  nothing rather than offering and then refusing. */}
              {day >= today && (
                <button
                  type="button"
                  aria-label={`Book an appointment on ${formatVisitDate(day)}`}
                  onClick={() => onBook(day)}
                  className="grid size-touch flex-none place-items-center rounded-md text-fg-subtle hover:bg-hover hover:text-accent"
                >
                  <Icon name="plus" size="1.125rem" />
                </button>
              )}
            </div>

            <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-faint">
              {entries.map((entry, index) => {
                const meta = KIND_META[entry.kind]
                const to = entryHref(entry)
                const body = (
                  <>
                    <span className={cx('mt-1.5 size-2.5 flex-none rounded-full', meta.dot)} />
                    <span className="flex min-w-0 flex-col">
                      <span className="font-medium">{entry.title}</span>
                      <span className="text-xs text-fg-subtle">
                        {/* The kind in words beside the dot, because a colour
                            on its own is not a label. */}
                        {[meta.label, entry.subtitle]
                          .filter((part, i, all) => part && all.indexOf(part) === i)
                          .join(' · ')}
                      </span>
                    </span>
                  </>
                )
                const shell =
                  'flex min-h-touch items-start gap-3 px-4 py-3 text-sm text-fg no-underline'
                return (
                  <li key={`${entry.kind}-${entry.title}-${index}`}>
                    {to ? (
                      <Link className={cx(shell, 'hover:bg-hover')} to={to}>
                        {body}
                      </Link>
                    ) : (
                      <span className={shell}>{body}</span>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
